import { Check, Copy } from 'lucide-react';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThemedToken } from 'shiki/core';
import { toast } from 'sonner';

import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { highlightLines, type CodeLanguage } from './code-block-highlighter';

export interface CodeBlockProps {
  code: string;
  language: CodeLanguage;
  /** Accessible name of the block, e.g. "Example response". */
  label?: string;
  className?: string;
}

/**
 * A runtime code block (JSON examples, snippets) with a copy button. It renders the plain text at
 * once and swaps in shiki's tokens when the lazy highlighter has loaded.
 */
export function CodeBlock({ code, language, label, className }: CodeBlockProps) {
  const { t } = useTranslation('docs');
  const [highlighted, setHighlighted] = useState<{
    code: string;
    lines: ThemedToken[][];
  } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let current = true;
    highlightLines(code, language).then(
      (lines) => {
        if (current && lines) setHighlighted({ code, lines });
      },
      () => undefined, // Plain text stays; highlighting is decoration.
    );
    return () => {
      current = false;
    };
  }, [code, language]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('mdx.copyFailed'));
    }
  };

  const lines = highlighted?.code === code ? highlighted.lines : null;

  return (
    <div className={cn('group relative', className)}>
      <pre
        role={label ? 'region' : undefined}
        aria-label={label}
        data-language={language}
        data-highlighted={lines ? 'true' : 'false'}
        tabIndex={0}
        className={cn(
          'bg-muted/40 max-h-[32rem] overflow-auto rounded-lg border p-4 font-mono text-[0.8125rem] leading-6',
          lines && 'shiki',
        )}
      >
        <code>
          {lines
            ? lines.map((line, index) => (
                <span key={index} className="block min-h-6">
                  {line.map((token, i) => (
                    <span key={i} style={token.htmlStyle as CSSProperties}>
                      {token.content}
                    </span>
                  ))}
                </span>
              ))
            : code}
        </code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={copied ? t('mdx.copied') : t('mdx.copy')}
        onClick={() => void copy()}
        className="bg-background/80 absolute top-2 right-2 size-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
      </Button>
    </div>
  );
}
